import { useState, useRef, useEffect } from 'react';
import TransactionRow from './TransactionRow.jsx';
import LoadingSpinner from './LoadingSpinner.jsx';
import Button from './Button.jsx';
import TransactionDetailSlideOver from './TransactionDetailSlideOver.jsx';
import BulkActionBar from './BulkActionBar.jsx';
import { useBulkPatchTransactionCategory } from '../hooks/useTransactions.js';

/**
 * Case « Tout sélectionner » de l'en-tête desktop — coché / partiellement
 * coché (aria-checked="mixed") / décoché selon l'état de la sélection sur la
 * page affichée.
 */
function SelectAllHeaderCheckbox({ count, total, onToggle }) {
  const state = count === 0 ? 'false' : count >= total ? 'true' : 'mixed';
  return (
    <span
      role="checkbox"
      aria-checked={state}
      tabIndex={0}
      aria-label="Tout sélectionner"
      title="Tout sélectionner"
      onClick={onToggle}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggle(); } }}
      className="inline-flex w-4 h-4 rounded border border-slate-300 bg-white items-center justify-center cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-500"
    >
      {state === 'true' && <span className="w-2 h-2 bg-slate-800 rounded-sm" aria-hidden />}
      {state === 'mixed' && <span className="w-2 h-0.5 bg-slate-800" aria-hidden />}
    </span>
  );
}

export default function TransactionList({
  transactions = [],
  isLoading,
  pagination,
  onPageChange,
  categories = [],
  selectable = false,
  emptyMessage = 'Aucune transaction',
}) {
  // On garde l'id en state et on re-derive l'objet tx à chaque render :
  // garantit que le SlideOver reçoit les dernières data après refetch
  // (changement de catégorie, marquage récurrence, etc.).
  const [selectedId, setSelectedId] = useState(null);
  const selected = selectedId
    ? transactions.find((t) => t.id === selectedId) ?? null
    : null;

  // Sélection multiple (checkboxes sur les logos) — distincte de selectedId
  // (transaction ouverte dans le détail).
  const [checkedIds, setCheckedIds] = useState(() => new Set());
  const [applyError, setApplyError] = useState(null);
  const [confirmation, setConfirmation] = useState(null);
  const bulkPatch = useBulkPatchTransactionCategory();

  // Vide la sélection dès que la liste affichée change (page suivante, filtre…) ;
  // un simple refetch renvoyant les mêmes ids ne la touche pas.
  const idsKey = transactions.map((t) => t.id).join(',');
  const prevIdsKeyRef = useRef(idsKey);
  useEffect(() => {
    if (prevIdsKeyRef.current !== idsKey) {
      setCheckedIds(new Set());
    }
    prevIdsKeyRef.current = idsKey;
  }, [idsKey]);

  function toggleSelect(tx) {
    setCheckedIds((prev) => {
      const next = new Set(prev);
      if (next.has(tx.id)) next.delete(tx.id);
      else next.add(tx.id);
      return next;
    });
  }

  function toggleSelectAll() {
    setCheckedIds((prev) =>
      prev.size >= transactions.length ? new Set() : new Set(transactions.map((t) => t.id))
    );
  }

  function handleClearSelection() {
    setCheckedIds(new Set());
    setApplyError(null);
  }

  function handleApply(categoryId) {
    const ids = [...checkedIds];
    setApplyError(null);
    bulkPatch.mutate(
      { transaction_ids: ids, category_id: categoryId },
      {
        onSuccess: (data) => {
          const n = data?.updated_count ?? ids.length;
          setCheckedIds(new Set());
          setConfirmation(`${n} transaction${n > 1 ? 's' : ''} catégorisée${n > 1 ? 's' : ''}`);
          setTimeout(() => setConfirmation(null), 4000);
        },
        onError: (err) => {
          setApplyError(err);
        },
      }
    );
  }

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner />
      </div>
    );
  }

  if (transactions.length === 0) {
    return (
      <div className="text-center py-12 text-sm text-slate-500">
        {emptyMessage}
      </div>
    );
  }

  const currentPage = pagination?.page ?? 1;
  const totalPages = pagination?.total_pages ?? 1;
  const showBar = selectable && checkedIds.size > 0;

  return (
    <div>
      {/* Header de colonnes desktop uniquement */}
      <div className="hidden md:grid grid-cols-[40px_100px_minmax(180px,1.5fr)_minmax(160px,1fr)_140px] gap-4 items-center px-4 py-2 border-b border-slate-200 bg-slate-50 text-[11px] font-semibold text-slate-500 uppercase tracking-wide">
        <div>
          {selectable && (
            <SelectAllHeaderCheckbox
              count={checkedIds.size}
              total={transactions.length}
              onToggle={toggleSelectAll}
            />
          )}
        </div>
        <div>Date</div>
        <div>Fournisseur</div>
        <div>Catégorie</div>
        <div className="text-right">Montant</div>
      </div>

      {confirmation && (
        <p role="status" className="px-4 py-2 text-sm text-green-700 bg-green-50 border-b border-green-100">
          {confirmation}
        </p>
      )}

      <div className={`divide-y divide-slate-100 ${showBar ? 'mb-28 lg:mb-24' : ''}`}>
        {transactions.map((tx) => (
          <TransactionRow
            key={tx.id}
            transaction={tx}
            categories={categories}
            onClick={(t) => setSelectedId(t.id)}
            selectable={selectable}
            selected={checkedIds.has(tx.id)}
            onToggleSelect={toggleSelect}
          />
        ))}
      </div>
      <TransactionDetailSlideOver
        transaction={selected}
        open={!!selected}
        onClose={() => setSelectedId(null)}
      />
      {totalPages > 1 && (
        <div className={`flex items-center justify-between px-4 py-3 border-t border-slate-100 ${showBar ? 'mb-28 lg:mb-24' : ''}`}>
          <Button
            variant="ghost"
            className="text-xs px-3 py-1.5"
            disabled={currentPage <= 1}
            onClick={() => onPageChange?.(currentPage - 1)}
          >
            Précédent
          </Button>
          <span className="text-xs text-slate-500">
            Page {currentPage} / {totalPages}
          </span>
          <Button
            variant="ghost"
            className="text-xs px-3 py-1.5"
            disabled={currentPage >= totalPages}
            onClick={() => onPageChange?.(currentPage + 1)}
          >
            Suivant
          </Button>
        </div>
      )}
      {showBar && (
        <BulkActionBar
          count={checkedIds.size}
          totalOnPage={transactions.length}
          onSelectAll={toggleSelectAll}
          onClear={handleClearSelection}
          onApply={handleApply}
          isPending={bulkPatch.isPending}
          error={applyError}
          categories={categories}
        />
      )}
    </div>
  );
}
